import type { DealDetail, PaperItem } from "./opsPaperworkTypes";
import { OpsDealAuditMenu } from "./OpsDealAuditMenu";
import { OpsDealIdentityStrip } from "./OpsDealSalespersonBanner";
import { OpsDealPacketSlider } from "./OpsDealPacketSlider";
import { OpsDealReadyToDeliverCard } from "./OpsDealReadyToDeliverCard";
import { OpsDealSubmitToBuildCard } from "./OpsDealSubmitToBuildCard";
import { OpsDealTypeMatchCard } from "./OpsDealTypeMatchCard";
import { OpsDealDriveFolderLink } from "./OpsDealDriveFolderLink";
import { OpsPaperworkAttachmentStrip } from "./OpsPaperworkAttachmentStrip";
import { OpsFinancialsFold } from "./OpsFinancialsFold";
import { OpsPaperworkDealsList } from "./OpsPaperworkDealsList";
import { useOpsDealsList } from "./useOpsDealsList";
import { formatDealReceivedAt } from "@/lib/ops/opsDealsListQuery";

export function OpsPaperworkDealsTab(props: {
  loading: boolean;
  selectedId: string | null;
  detail: DealDetail | null;
  canManageSetup: boolean;
  newDealName: string;
  setNewDealName: (v: string) => void;
  setSelectedId: (id: string | null) => void;
  onCreateDeal: () => Promise<void>;
  onSeedTemplates: () => Promise<void>;
  onReseedDeal: (id: string) => Promise<void>;
  onDeleteDeal: (id: string) => Promise<void>;
  onSavePaperRow: (row: PaperItem, patch: Record<string, unknown>) => Promise<void>;
  canEditAttachments: boolean;
  onReloadDetail: () => Promise<void>;
  onOpenPayables?: () => void;
  salespersonBusy?: boolean;
  onInviteSalesperson?: () => void;
  onRequestSalespersonInvite?: () => void;
  onAddSalespersonLocation?: () => void;
  typeMatchBusy?: boolean;
  onAssignDealType?: (productTypeId: string, saleTypeId: string | null) => Promise<void>;
  onRematchDealType?: () => Promise<void>;
}) {
  const list = useOpsDealsList({ active: !props.selectedId });
  if (!props.selectedId) {
    return (
      <div className="space-y-3">
        <OpsFinancialsFold enabled={props.canManageSetup} />
        <OpsPaperworkDealsList
          list={list}
          parentBusy={props.loading}
          canCreate={props.canManageSetup}
          newDealName={props.newDealName}
          setNewDealName={props.setNewDealName}
          setSelectedId={props.setSelectedId}
          onCreateDeal={props.onCreateDeal}
          onSeedTemplates={props.onSeedTemplates}
        />
      </div>
    );
  }
  if (!props.detail) return null;
  const selectedId = props.selectedId;
  const allAttachments = props.detail.attachments ?? [];
  const dealLevelAttachments = allAttachments.filter((a) => a.paperwork_item_id === null);
  const attachmentsByItem = new Map<string, typeof allAttachments>();
  for (const a of allAttachments) {
    if (a.paperwork_item_id) {
      const list = attachmentsByItem.get(a.paperwork_item_id) ?? [];
      list.push(a);
      attachmentsByItem.set(a.paperwork_item_id, list);
    }
  }
  const attachmentCountsByItemKey = new Map<string, number>();
  for (const item of props.detail.paperwork_items) {
    const n = attachmentsByItem.get(item.id)?.length ?? 0;
    if (n) attachmentCountsByItemKey.set(item.item_key, n);
  }
  const received = formatDealReceivedAt(
    typeof props.detail.deal.created_at === "string" ? props.detail.deal.created_at : null
  );
  const salesperson = props.detail.salesperson;
  return (
    <div className="space-y-4">
      <OpsFinancialsFold enabled={props.canManageSetup} />
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" className="text-sm text-indigo-600" onClick={() => props.setSelectedId(null)}>← All deals</button>
        <span className="font-semibold">{(props.detail.deal.display_name as string) || selectedId}</span>
        <OpsDealDriveFolderLink deal={props.detail.deal} />
        {received ? <span className="text-xs text-gray-500">Received {received}</span> : null}
        {props.canManageSetup ? (
          <button type="button" className="text-xs border rounded px-2 py-0.5" onClick={() => void props.onReseedDeal(selectedId)}>Re-seed checklist from templates</button>
        ) : null}
        {props.canManageSetup && props.onOpenPayables ? (
          <button type="button" className="text-xs border rounded px-2 py-0.5" onClick={props.onOpenPayables}>
            Manufacturer payable
          </button>
        ) : null}
        {props.canManageSetup ? (
          <button type="button" className="text-xs border rounded px-2 py-0.5 text-red-700" onClick={() => void props.onDeleteDeal(selectedId)}>Delete deal</button>
        ) : null}
      </div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        {salesperson ? (
          <OpsDealIdentityStrip
            salesperson={salesperson}
            busy={props.salespersonBusy === true}
            onInvite={props.onInviteSalesperson}
            onRequest={props.onRequestSalespersonInvite}
            onAddLocation={props.onAddSalespersonLocation}
          />
        ) : null}
        <section className="ml-auto min-w-0 max-w-md text-right">
          <div className="mb-1 flex items-center justify-end gap-2">
            <h3 className="text-sm font-semibold text-gray-800">Tasks on this deal</h3>
            <OpsDealAuditMenu
              timeline={props.detail.timeline_events}
              externalIds={props.detail.external_ids}
            />
          </div>
          <ul className="flex flex-wrap justify-end gap-x-4 gap-y-1 text-sm">
            {props.detail.tasks.map((t) => (
              <li key={t.id} className="flex items-baseline justify-end gap-2">
                <span>{t.title}</span>
                <span className="text-xs text-gray-500">{t.status}</span>
              </li>
            ))}
            {props.detail.tasks.length === 0 ? <li className="text-gray-500">No tasks linked.</li> : null}
          </ul>
        </section>
      </div>
      <div className="flex flex-wrap items-stretch gap-3">
        {props.onAssignDealType && props.onRematchDealType ? (
          <div className="min-w-[18rem] flex-1">
            <OpsDealTypeMatchCard
              match={props.detail.deal_type_match ?? null}
              catalog={props.detail.deal_type_catalog ?? null}
              busy={props.typeMatchBusy === true}
              onAssign={props.onAssignDealType}
              onRematch={props.onRematchDealType}
            />
          </div>
        ) : null}
        <div className="min-w-[18rem] flex-1">
          <OpsDealSubmitToBuildCard
            dealId={selectedId}
            deal={props.detail.deal}
            items={props.detail.paperwork_items}
            attachmentCounts={attachmentCountsByItemKey}
            linkedUnit={props.detail.linked_unit ?? null}
            onChanged={props.onReloadDetail}
          />
        </div>
        <div className="min-w-[18rem] flex-1">
          <OpsDealReadyToDeliverCard
            dealId={selectedId}
            deal={props.detail.deal}
            linkedUnit={props.detail.linked_unit ?? null}
            onChanged={props.onReloadDetail}
          />
        </div>
      </div>
      <details className="rounded-lg border border-gray-200 p-3" open={dealLevelAttachments.length > 0}>
        <summary className="text-sm font-semibold text-gray-800 cursor-pointer">Deal-level files {dealLevelAttachments.length ? <span className="text-xs text-gray-500 font-normal">({dealLevelAttachments.length})</span> : null}</summary>
        <div className="mt-2">
          <OpsPaperworkAttachmentStrip
            dealId={selectedId}
            paperworkItemId={null}
            attachments={dealLevelAttachments}
            canEdit={props.canEditAttachments}
            onChanged={props.onReloadDetail}
          />
        </div>
      </details>
      <OpsDealPacketSlider
        dealId={selectedId}
        items={props.detail.paperwork_items}
        attachmentsByItem={attachmentsByItem}
        canEditAttachments={props.canEditAttachments}
        onSavePaperRow={props.onSavePaperRow}
        onReloadDetail={props.onReloadDetail}
      />
    </div>
  );
}
